/**
 * YARDIM YAZISI GÖVDESİ → BLOKLAR — UI/UX Dalga 2 (M10)
 *
 * ================================================================
 * NEDEN AYRIŞTIRICI, MARKDOWN DEĞİL
 * ================================================================
 * Yazılar düz metin ve yönetici panelinden düzenlenebiliyor (F-HLP-13).
 * Markdown çözümleyicisi eklemek, düzenleyene biçimlendirme gücü (bağlantı
 * gömme dahil) vermek demek — `Yardim.tsx`in eski notu bunu zaten reddediyordu.
 * Bunun yerine yazıların ZATEN uyduğu küçük bir yazım kuralı okunuyor
 * (`yardim-yazilari.ts` başlığı "YAZIM KURALI"):
 *
 *   - "Menüden: A › B › C"        → menü yolu satırı
 *   - "1. …", "2. …"              → numaralı adım (her satır tek eylem)
 *   - "Neden böyle: …"            → gerekçe notu
 *   - "Sık karşılaşılanlar"       → ardından "· hata → çözüm" satırları
 *   - "İlgili yazılar: a · b"     → ilgili yazı başlıkları
 *   - kalan her şey               → paragraf
 *
 * KURALA UYMAYAN (eski biçimli) yazı kaybolmaz: bütün paragrafları düz paragraf
 * bloğu olur ve eskisi gibi görünür. Ayrıştırıcı hiçbir satırı DÜŞÜRMEZ.
 */

export type YardimBlogu =
  | { tur: "paragraf"; metin: string }
  | { tur: "yol"; adimlar: string[] }
  | { tur: "adimlar"; adimlar: string[] }
  | { tur: "neden"; metin: string }
  | { tur: "siklar"; maddeler: Array<{ sorun: string; cozum: string | null }> }
  | { tur: "ilgili"; basliklar: string[] };

const ADIM = /^(\d+)\.\s+(.*)$/;
const YOL = /^Menüden:\s*(.+)$/;
const NEDEN = /^Neden böyle:\s*(.*)$/;
const SIK_BASLIK = /^Sık karşılaşılanlar\s*$/;
const ILGILI = /^İlgili yazılar:\s*(.+)$/;
const MADDE = /^[·•]\s*(.+)$/;

export function yardimGovdesiniAyir(govde: string): YardimBlogu[] {
  const bloklar: YardimBlogu[] = [];
  const satirlar = govde.replace(/\r\n/g, "\n").split("\n");
  let paragraf: string[] = [];
  let adimlar: string[] | null = null;
  let siklar: Array<{ sorun: string; cozum: string | null }> | null = null;

  const paragrafiKapat = () => {
    if (paragraf.length > 0) bloklar.push({ tur: "paragraf", metin: paragraf.join("\n") });
    paragraf = [];
  };
  const adimlariKapat = () => {
    if (adimlar) bloklar.push({ tur: "adimlar", adimlar });
    adimlar = null;
  };
  const siklariKapat = () => {
    if (siklar && siklar.length > 0) bloklar.push({ tur: "siklar", maddeler: siklar });
    siklar = null;
  };
  const hepsiniKapat = () => {
    paragrafiKapat();
    adimlariKapat();
    siklariKapat();
  };

  for (const ham of satirlar) {
    const satir = ham.trim();

    if (satir === "") {
      // Boş satır paragrafı ve adım listesini kapatır; sık sorulanlar listesi
      // başlığın ardından gelen maddelerle sürüyor (arada boş satır olmaz).
      paragrafiKapat();
      adimlariKapat();
      continue;
    }

    const yol = YOL.exec(satir);
    if (yol) {
      hepsiniKapat();
      bloklar.push({
        tur: "yol",
        adimlar: yol[1]!
          .split("›")
          .map((p) => p.trim())
          .filter(Boolean),
      });
      continue;
    }

    const adim = ADIM.exec(satir);
    if (adim) {
      paragrafiKapat();
      siklariKapat();
      (adimlar ??= []).push(adim[2]!);
      continue;
    }

    const neden = NEDEN.exec(satir);
    if (neden) {
      hepsiniKapat();
      bloklar.push({ tur: "neden", metin: neden[1]! });
      continue;
    }

    if (SIK_BASLIK.test(satir)) {
      hepsiniKapat();
      siklar = [];
      continue;
    }

    const madde = MADDE.exec(satir);
    if (madde && siklar) {
      const [sorun, ...gerisi] = madde[1]!.split(" → ");
      siklar.push({
        sorun: sorun!.trim(),
        cozum: gerisi.length > 0 ? gerisi.join(" → ").trim() : null,
      });
      continue;
    }

    const ilgili = ILGILI.exec(satir);
    if (ilgili) {
      hepsiniKapat();
      bloklar.push({
        tur: "ilgili",
        basliklar: ilgili[1]!
          .split(" · ")
          .map((b) => b.trim())
          .filter(Boolean),
      });
      continue;
    }

    // Sık-karşılaşılanlar listesi dışındaki her satır paragraf: hiçbir şey düşmüyor.
    siklariKapat();
    adimlariKapat();
    paragraf.push(satir);
  }

  hepsiniKapat();
  return bloklar;
}

/** Yazının menü yolu (varsa) — "Ekrana git" ve arama sonuçları için. */
export function yardimMenuYolu(govde: string): string[] | null {
  const blok = yardimGovdesiniAyir(govde).find((b) => b.tur === "yol");
  return blok && blok.tur === "yol" ? blok.adimlar : null;
}
